// The optimistic status a row holds while ITS RPC is out, shared by the list and
// the detail panel — they are siblings, so neither can own it. Keyed per machine:
// installing Claude on ceiba says nothing about Claude on nogal.
import {
  RECHECK_THROTTLE_MS,
  providerTransition,
  type ProviderFlowEvent,
  type ProviderFlowGuards,
  type ProviderFlowResult,
} from "./provider-transitions.js";
import type { ProviderRowStatus } from "./provider-status.js";
import {
  isInstallStage,
  isInstallStageReport,
  type InstallStage,
  type InstallStageReport,
} from "./provider-install-progress.js";
import { accountSync, workspaceSync } from "$lib/plugins/agents/sync-session.js";

function key(machine: string | null | undefined, providerId: string): string {
  return `${machine ?? "-"}:${providerId}`;
}

class ProviderFlowState {
  #status = $state<Record<string, ProviderRowStatus>>({});
  #notes = $state<Record<string, string>>({});
  #binaries = $state<Record<string, string>>({});
  #recheckedAt = $state<Record<string, number>>({});
  #progress = $state<Record<string, Partial<Record<InstallStage, InstallStageReport>>>>({});
  #installStartedAt = $state<Record<string, number>>({});
  #now = $state(Date.now());
  #timer: ReturnType<typeof setInterval> | null = null;

  statusOf(machine: string | null | undefined, providerId: string): ProviderRowStatus | undefined {
    return this.#status[key(machine, providerId)];
  }

  noteOf(machine: string | null | undefined, providerId: string): string | undefined {
    return this.#notes[key(machine, providerId)];
  }

  /** The binary the daemon looked for. The client cannot guess it: the catalog
   *  id is `cursor` and the binary is `cursor-agent`. */
  binaryOf(machine: string | null | undefined, providerId: string): string | undefined {
    return this.#binaries[key(machine, providerId)];
  }

  setBinary(machine: string | null | undefined, providerId: string, binary: string): void {
    this.#binaries[key(machine, providerId)] = binary;
  }

  overridesFor(machine: string | null | undefined): ReadonlyMap<string, ProviderRowStatus> {
    const prefix = `${machine ?? "-"}:`;
    const out = new Map<string, ProviderRowStatus>();
    for (const [k, v] of Object.entries(this.#status)) {
      if (k.startsWith(prefix)) out.set(k.slice(prefix.length), v);
    }
    return out;
  }

  set(
    machine: string | null | undefined,
    providerId: string,
    status: ProviderRowStatus,
    note?: string,
  ): void {
    const id = key(machine, providerId);
    this.#status[id] = status;
    if (note) this.#notes[id] = note;
    else delete this.#notes[id];
  }

  /** Statuses the daemon's own probe can express. Everything else — a failed
   *  install, a missing ACP CLI, a rejected key — is CLIENT knowledge a probe
   *  would silently erase, so it survives until the user acts again. */
  static readonly PROBE_EXPRESSIBLE = new Set<ProviderRowStatus>([
    "ready",
    "acp-ready",
    "signed-out",
  ]);

  /** Hand the row back to the probe, but only if the probe can say the same thing. */
  releaseIfProbeable(machine: string | null | undefined, providerId: string): void {
    const current = this.statusOf(machine, providerId);
    if (current && ProviderFlowState.PROBE_EXPRESSIBLE.has(current)) {
      this.release(machine, providerId);
    }
  }

  release(machine: string | null | undefined, providerId: string): void {
    const id = key(machine, providerId);
    delete this.#status[id];
    delete this.#notes[id];
    delete this.#binaries[id];
  }

  /** The stages the machine reported for its current install of this provider. */
  progressOf(
    machine: string | null | undefined,
    providerId: string,
  ): Partial<Record<InstallStage, InstallStageReport>> {
    return this.#progress[key(machine, providerId)] ?? {};
  }

  installStartedAt(machine: string | null | undefined, providerId: string): number | undefined {
    return this.#installStartedAt[key(machine, providerId)];
  }

  /** A cyborg:install_provider_cli_progress frame. Unknown stages (a newer daemon) are ignored. */
  noteProgress(machine: string, providerId: string, stage: unknown, state: unknown): void {
    if (!isInstallStage(stage) || !isInstallStageReport(state)) return;
    const id = key(machine, providerId);
    this.#progress[id] = { ...this.#progress[id], [stage]: state };
  }

  /** Run the transition table and commit the move. Refusals are returned, never applied. */
  apply(
    machine: string | null | undefined,
    providerId: string,
    from: ProviderRowStatus,
    event: ProviderFlowEvent,
    guards: ProviderFlowGuards,
  ): ProviderFlowResult {
    const result = providerTransition(from, event, guards);
    if (result.kind === "move") this.set(machine, providerId, result.to, result.note);
    // A new install starts from a clean slate: the last attempt's stages are not this one's.
    if (result.kind === "move" && event.type === "install") {
      const id = key(machine, providerId);
      this.#progress[id] = {};
      this.#installStartedAt[id] = Date.now();
    }
    return result;
  }

  markRechecked(machine: string | null | undefined): void {
    this.#recheckedAt[machine ?? "-"] = Date.now();
    this.#startTicking();
  }

  /** Seconds left on this machine's 30 s budget — what the button counts down. */
  throttleRemaining(machine: string | null | undefined): number {
    const last = this.#recheckedAt[machine ?? "-"];
    if (last === undefined) return 0;
    const left = RECHECK_THROTTLE_MS - (this.#now - last);
    return left > 0 ? Math.ceil(left / 1000) : 0;
  }

  canRecheck(machine: string | null | undefined): boolean {
    return this.throttleRemaining(machine) === 0;
  }

  #startTicking(): void {
    if (this.#timer !== null) return;
    this.#timer = setInterval(() => {
      this.#now = Date.now();
      const armed = Object.keys(this.#recheckedAt).some((m) => this.throttleRemaining(m) > 0);
      if (!armed) this.#stopTicking();
    }, 1000);
  }

  #stopTicking(): void {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Tests and the auth-context reset: forget every machine's optimism. */
  clear(): void {
    this.#status = {};
    this.#notes = {};
    this.#binaries = {};
    this.#recheckedAt = {};
    this.#progress = {};
    this.#installStartedAt = {};
    this.#stopTicking();
  }
}

export const providerFlow = new ProviderFlowState();
for (const boundary of [accountSync, workspaceSync]) {
  boundary.adopt("providerFlow", providerFlow, (flow) => flow.clear());
}
